import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { ChevronDown, HelpCircle, Shield, Zap, Users, CreditCard, Globe, Laptop, Clock, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import FAQComparison from "../components/FAQComparison.jsx";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const stagger = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
};

// FAQ categories with icons
const faqCategories = [
  { id: "general", name: "General", icon: HelpCircle },
  { id: "privacy", name: "Privacy & Security", icon: Shield },
  { id: "performance", name: "Performance", icon: Zap },
  { id: "pricing", name: "Pricing & Billing", icon: CreditCard },
  { id: "setup", name: "Setup & Devices", icon: Laptop },
  { id: "features", name: "Features", icon: Globe },
];

const faqs = [
  // General
  {
    category: "general",
    q: "What is SACVPN?",
    a: "SACVPN is a VPN service powered by the WireGuard protocol. We provide secure and private internet access for individuals, gamers, and businesses. Our service encrypts your internet traffic, masks your IP address, and protects your online privacy.",
  },
  {
    category: "general",
    q: "How many devices can I use?",
    a: "The Personal / Gaming plan allows unlimited devices, so you can protect your entire household with a single subscription. Business tiers include fixed quotas (10, 50, 100+ devices) based on your plan level.",
  },
  {
    category: "general",
    q: "What is the 14-day free trial?",
    a: "We offer a 14-day free trial so you can experience SACVPN risk-free. No credit card is required to start your trial. After 14 days, you can choose to subscribe to continue your protection.",
  },

  // Privacy & Security
  {
    category: "privacy",
    q: "Do you keep logs of my activity?",
    a: "We do not log your online activity: we never track or store the websites you visit, your DNS queries, or the contents of your traffic. WireGuard does not record where your packets go once they leave the tunnel, so that history is simply never written down. To run the service and show you your own devices, we do process limited connection metadata (connection status, last handshake time, assigned VPN IP, the IP your device connects from, and data transferred). Our Privacy Policy explains exactly what we do and do not keep.",
  },
  {
    category: "privacy",
    q: "What encryption does SACVPN use?",
    a: "We use WireGuard's encryption suite: ChaCha20 for symmetric encryption, Poly1305 for authentication, Curve25519 for key exchange, and BLAKE2s for hashing.",
  },
  {
    category: "privacy",
    q: "What VPN protocol do you use?",
    a: "WireGuard only. It's simpler and more modern than older VPN protocols like OpenVPN or IKEv2. WireGuard has roughly 4,000 lines of code compared to OpenVPN's 400,000+, making it easier to audit.",
  },
  {
    category: "privacy",
    q: "Is SACVPN safe on public Wi-Fi?",
    a: "Yes. SACVPN encrypts all your traffic, so it is far harder for anyone on a public Wi-Fi network to intercept your data. Whether you're at a coffee shop, airport, or hotel, your connection is encrypted in transit.",
  },

  // Performance
  {
    category: "performance",
    q: "Will SACVPN slow down my internet?",
    a: "WireGuard's efficient design means a light footprint, so most users won't notice a slowdown. The added latency depends mainly on your distance to the server you pick. Real-world speed and latency depend on distance to server, your ISP, and network conditions, so the best gauge is your own connection.",
  },
  {
    category: "performance",
    q: "Is SACVPN good for gaming?",
    a: "Yes. WireGuard's low overhead means minimal impact on gaming performance, and connecting to the SACVPN US node nearest you can help with latency and packet loss, though the effect depends on your distance to the server and the game's servers, so judge it by your own in-game ping. Routing your traffic through our servers also keeps your real IP off the table, so opponents cannot target it.",
  },
  {
    category: "performance",
    q: "Can I use SACVPN for streaming?",
    a: "Yes. We help unlock geo-restricted content and reduce ISP throttling while streaming. Access content from different regions with a single click. Streaming quality still depends on your own connection.",
  },
  {
    category: "performance",
    q: "Where are your servers located?",
    a: "We run 3 US nodes (Texas, Virginia, and Dallas) with automatic failover, positioned for sensible low-latency routing across the US. We add regions as demand grows.",
  },

  // Pricing & Billing
  {
    category: "pricing",
    q: "Do you offer a free trial?",
    a: "Yes. We offer a 14-day free trial with no credit card required. Try SACVPN completely risk-free. After your trial, we also provide a 30-day money-back guarantee if you're unsatisfied.",
  },
  {
    category: "pricing",
    q: "What payment methods do you accept?",
    a: "We accept all major credit cards (Visa, MasterCard, American Express, Discover) processed securely through Stripe. Additional payment methods including PayPal and cryptocurrency are coming soon.",
  },
  {
    category: "pricing",
    q: "How does Business pricing work?",
    a: "Business plans are priced by device quota: 10 devices, 50 devices, or 100+ devices. Admins can add or revoke devices and manage employee access from a centralized dashboard. Enterprise plans include a custom SLA negotiated at contract and priority email support.",
  },
  {
    category: "pricing",
    q: "Can I cancel anytime?",
    a: "Yes, you can cancel your subscription at any time from your dashboard. Your service continues until the end of your billing period. No cancellation fees, no hassles.",
  },
  {
    category: "pricing",
    q: "What's your refund policy?",
    a: "We offer a 30-day money-back guarantee for first-time subscribers. If you're not satisfied within the first 30 days after your trial ends, contact us for a full refund.",
  },

  // Setup & Devices
  {
    category: "setup",
    q: "How do I set up SACVPN?",
    a: "After creating a device in your dashboard, you'll receive a QR code and config file. Simply scan the QR code in the WireGuard app or import the config file. Setup takes less than 60 seconds on any platform.",
  },
  {
    category: "setup",
    q: "What devices are supported?",
    a: "SACVPN works on Windows, macOS, Linux, iOS, Android, and routers. Any device that supports WireGuard can use SACVPN. We provide step-by-step setup guides for each platform.",
  },
  {
    category: "setup",
    q: "Can I use SACVPN on my router?",
    a: "Yes. Router-level VPN protects all devices on your network, including smart TVs, gaming consoles, and IoT devices. We support routers running OpenWrt, pfSense, ASUS-WRT, and other WireGuard-compatible firmware.",
  },
  {
    category: "setup",
    q: "Do you offer support?",
    a: "Yes. We provide US-based email support and read every ticket ourselves, with replies within 24 hours and often much sooner during business hours (CT). We focus on practical resolution: getting you online, optimizing performance, and solving issues quickly.",
  },

  // Features
  {
    category: "features",
    q: "What makes SACVPN different from other VPNs?",
    a: "We focus on performance, simplicity, and honesty. A WireGuard-only protocol, unlimited devices on personal plans, transparent pricing with no hidden fees, and a genuine no-logs policy enforced by design.",
  },
  {
    category: "features",
    q: "Does SACVPN have a kill switch?",
    a: "The official WireGuard desktop apps (Windows and macOS) automatically block untunneled traffic (a kill switch) for full-tunnel configs like ours, so your internet is blocked if the tunnel drops. On Linux, routers, and mobile you enable the kill switch or the always-on/block-without-VPN setting yourself. We recommend turning it on.",
  },
  {
    category: "features",
    q: "Can I use SACVPN for torrenting?",
    a: "Yes, P2P traffic is allowed on all our US servers. Your activity is protected by our no-logs policy and encrypted connection.",
  },
];

function FAQItem({ faq, isOpen, onClick }) {
  return (
    <motion.div
      variants={fadeUp}
      className="rounded-xl border border-white/[0.08] bg-dark-900 overflow-hidden hover:border-brand-500/30 transition-colors"
    >
      <button
        onClick={onClick}
        className="w-full px-6 py-5 text-left flex items-center justify-between gap-4 hover:bg-white/[0.02] transition-colors"
      >
        <span className="font-semibold text-white">{faq.q}</span>
        <ChevronDown
          className={`w-5 h-5 text-brand-400 transition-transform duration-300 flex-shrink-0 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="px-6 pb-5 text-gray-400 leading-relaxed border-t border-white/[0.06] pt-4">
              {faq.a}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default function FAQ() {
  const [activeCategory, setActiveCategory] = useState("general");
  const [openFaq, setOpenFaq] = useState(null);

  const filteredFaqs = faqs.filter((faq) => faq.category === activeCategory);
  const activeCategoryInfo = faqCategories.find((c) => c.id === activeCategory);

  return (
    <>
      <Helmet>
        <title>VPN FAQ - Frequently Asked Questions | SACVPN</title>
        <meta
          name="description"
          content="Get answers to common questions about SACVPN. Learn about our WireGuard VPN service, no-logs policy, 14-day free trial, pricing, device setup, and more."
        />
        <meta
          name="keywords"
          content="VPN FAQ, SACVPN questions, WireGuard VPN help, VPN setup guide, VPN pricing questions, no-logs VPN, VPN free trial"
        />
        <link rel="canonical" href="https://www.sacvpn.com/faq" />
        <meta property="og:title" content="VPN FAQ - Frequently Asked Questions | SACVPN" />
        <meta property="og:description" content="Get answers to common questions about SACVPN. Learn about our WireGuard VPN service, no-logs policy, 14-day free trial, pricing, device setup, and more." />
        {/* FAQPage Schema for Rich Snippets */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map(faq => ({
              "@type": "Question",
              "name": faq.q,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.a
              }
            }))
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 bg-dark-900" />
        <div className="absolute inset-0 bg-[radial-gradient(900px_460px_at_50%_-5%,rgba(14,165,233,0.12),transparent_65%)]" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.p variants={fadeUp} className="text-xs font-mono uppercase tracking-[0.2em] text-brand-400 mb-4">
              Help center
            </motion.p>

            <motion.h1
              variants={fadeUp}
              className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-white leading-[1.05] tracking-tight max-w-4xl mx-auto"
            >
              Frequently asked{" "}
              <span className="text-brand-400">questions</span>
            </motion.h1>

            <motion.p
              variants={fadeUp}
              className="mt-6 text-lg text-gray-400 max-w-2xl mx-auto leading-relaxed"
            >
              Everything you need to know about SACVPN. Can't find what you're looking for?{" "}
              <Link to="/contact" className="text-brand-400 hover:text-brand-300 font-medium transition-colors">
                Contact our support team
              </Link>
              .
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-20 bg-dark-900">
        <div className="max-w-7xl mx-auto px-6">
          {/* Category Tabs */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="flex flex-wrap justify-center gap-3 mb-12"
          >
            {faqCategories.map((category) => {
              const Icon = category.icon;
              const isActive = activeCategory === category.id;
              return (
                <motion.button
                  key={category.id}
                  variants={fadeUp}
                  onClick={() => {
                    setActiveCategory(category.id);
                    setOpenFaq(null);
                  }}
                  className={`flex items-center gap-2 px-5 py-3 rounded-lg font-medium transition-colors ${
                    isActive
                      ? "bg-brand-500 text-white"
                      : "bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10 hover:text-white"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {category.name}
                </motion.button>
              );
            })}
          </motion.div>

          {/* Active Category Header */}
          <motion.div
            key={activeCategory}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col items-center text-center mb-10"
          >
            <div className="w-11 h-11 rounded-lg bg-brand-500/10 border border-brand-500/20 flex items-center justify-center mb-4">
              {activeCategoryInfo && (
                <activeCategoryInfo.icon className="w-5 h-5 text-brand-400" />
              )}
            </div>
            <h2 className="text-2xl md:text-3xl font-display font-bold text-white tracking-tight">
              {activeCategoryInfo?.name}
            </h2>
          </motion.div>

          {/* FAQ List */}
          <motion.div
            key={activeCategory + "-list"}
            initial="hidden"
            animate="visible"
            variants={stagger}
            className="max-w-3xl mx-auto space-y-3"
          >
            {filteredFaqs.map((faq, index) => (
              <FAQItem
                key={index}
                faq={faq}
                isOpen={openFaq === index}
                onClick={() => setOpenFaq(openFaq === index ? null : index)}
              />
            ))}
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="mt-16 grid md:grid-cols-3 gap-6 max-w-4xl mx-auto"
          >
            <Link
              to="/download"
              className="group rounded-xl border border-white/[0.08] bg-dark-900 p-6 hover:border-brand-500/30 transition-colors"
            >
              <div className="w-11 h-11 rounded-lg bg-brand-500/10 border border-brand-500/20 flex items-center justify-center mb-4">
                <Clock className="w-5 h-5 text-brand-400" />
              </div>
              <h3 className="font-semibold text-white mb-2">Start free trial</h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                Try SACVPN free for 14 days. No credit card required.
              </p>
            </Link>

            <Link
              to="/blog"
              className="group rounded-xl border border-white/[0.08] bg-dark-900 p-6 hover:border-brand-500/30 transition-colors"
            >
              <div className="w-11 h-11 rounded-lg bg-brand-500/10 border border-brand-500/20 flex items-center justify-center mb-4">
                <Zap className="w-5 h-5 text-brand-400" />
              </div>
              <h3 className="font-semibold text-white mb-2">Read our blog</h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                In-depth guides and tips for online privacy and security.
              </p>
            </Link>

            <Link
              to="/contact"
              className="group rounded-xl border border-white/[0.08] bg-dark-900 p-6 hover:border-brand-500/30 transition-colors"
            >
              <div className="w-11 h-11 rounded-lg bg-brand-500/10 border border-brand-500/20 flex items-center justify-center mb-4">
                <Users className="w-5 h-5 text-brand-400" />
              </div>
              <h3 className="font-semibold text-white mb-2">Contact support</h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                Get help via US-based email support, with replies within 24 hours.
              </p>
            </Link>
          </motion.div>

          {/* Helpful Resources */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="mt-20"
          >
            <h3 className="text-2xl font-display font-bold text-white tracking-tight text-center mb-8">Helpful resources</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
              {[
                { to: "/blog/what-is-vpn-complete-guide", tag: "Guide", title: "What is a VPN? Complete guide" },
                { to: "/blog/wireguard-vs-openvpn-comparison", tag: "Comparison", title: "WireGuard vs OpenVPN" },
                { to: "/blog/vpn-setup-guide-beginners", tag: "Tutorial", title: "VPN setup guide for beginners" },
                { to: "/blog/how-to-choose-business-vpn", tag: "Business", title: "How to choose a business VPN" },
                { to: "/blog/public-wifi-security-risks", tag: "Security", title: "Public Wi-Fi security risks" },
                { to: "/industries/remote-teams", tag: "Industry", title: "VPN for remote teams" },
              ].map((r) => (
                <Link key={r.to} to={r.to} className="block rounded-xl border border-white/[0.08] bg-dark-900 p-5 hover:border-brand-500/30 transition-colors">
                  <span className="text-brand-400 text-xs font-mono uppercase tracking-[0.14em]">{r.tag}</span>
                  <h4 className="font-semibold text-white mt-1.5">{r.title}</h4>
                </Link>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Competitor Comparison */}
      <FAQComparison />

      {/* CTA Section */}
      <section className="py-24 bg-dark-900 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="relative overflow-hidden rounded-2xl border border-brand-500/20 bg-[radial-gradient(700px_320px_at_50%_-10%,rgba(14,165,233,0.18),transparent_70%)] bg-dark-800 px-8 md:px-14 py-16 text-center"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-white tracking-tight mb-4">
              Ready to protect your privacy?
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-9">
              Put your traffic on a private WireGuard network. Start your 14-day free trial today.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/download"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-lg bg-brand-500 text-white font-semibold hover:bg-brand-400 transition-colors"
              >
                <Clock className="w-5 h-5" />
                Start free trial
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-lg border border-white/15 text-white font-semibold hover:bg-white/5 transition-colors"
              >
                Contact us <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
}
